import type { SubagentOpenTarget } from '@terra/ui-design-system';
import { useCallback } from 'react';
import { terraProxyApiClient } from '../../clients/testerClient';
import { sidebarTabMapping } from '../layout/utils';
import { useBottomPanelStore } from '../../store/bottomPanelStore';
import type { OpenMainContentTabInput } from '../../store/layoutStoreTypes';

type EndpointRecord = {
  id: string;
  url?: string | null;
  path?: string | null;
};

export function useOpenSubagentActivity({
  applicationId,
  openOrFocusTab,
  revealMainContent,
}: {
  applicationId: string | undefined;
  openOrFocusTab: (tab: OpenMainContentTabInput) => void;
  revealMainContent: () => void;
}): (target: SubagentOpenTarget) => void {
  return useCallback(
    (target) => {
      revealMainContent();

      if (target.kind === 'file') {
        useBottomPanelStore.getState().setSelectedFileId(target.path);
        openOrFocusTab(sidebarTabMapping.code);

        return;
      }

      if (!applicationId) {
        return;
      }

      void terraProxyApiClient
        .getEndpointsByApplication(applicationId)
        .then((result) => {
          const match = pickEndpoint({
            endpoints: result?.endpoints ?? [],
            path: target.path,
          });

          if (!match) {
            return;
          }

          openOrFocusTab({
            id: `endpoint-${match.id}`,
            title: match.path || match.url || target.path,
            type: 'endpoint',
            data: {
              endpointId: match.id,
              endpointUrl: match.url ?? undefined,
            },
          });
        })
        .catch((error: unknown) => {
          console.error('Failed to open searched endpoint:', error);
        });
    },
    [applicationId, openOrFocusTab, revealMainContent],
  );
}

export function pickEndpoint({
  endpoints,
  path,
}: {
  endpoints: EndpointRecord[];
  path: string;
}): EndpointRecord | undefined {
  const needle = trimSlash(path);

  if (!needle) {
    return undefined;
  }

  const ranked = endpoints.flatMap((endpoint) => {
    const rank = endpointRank({ endpoint, needle });

    return rank === undefined ? [] : [{ endpoint, rank }];
  });

  ranked.sort(
    (left, right) =>
      left.rank - right.rank ||
      pathLength(left.endpoint) - pathLength(right.endpoint),
  );

  return ranked[0]?.endpoint;
}

function endpointRank({
  endpoint,
  needle,
}: {
  endpoint: EndpointRecord;
  needle: string;
}): number | undefined {
  const endpointPath = endpoint.path ? trimSlash(endpoint.path) : '';
  const urlPath = pathFromUrl(endpoint.url);

  if (endpointPath === needle || urlPath === needle) {
    return 0;
  }

  if (
    isBoundarySuffix(endpointPath, needle) ||
    isBoundarySuffix(urlPath, needle)
  ) {
    return 1;
  }

  return undefined;
}

function isBoundarySuffix(value: string, needle: string): boolean {
  if (!value.endsWith(needle) || value.length <= needle.length) {
    return false;
  }

  if (needle.startsWith('/')) {
    return true;
  }

  return value[value.length - needle.length - 1] === '/';
}

function pathLength(endpoint: EndpointRecord): number {
  return (endpoint.path ?? endpoint.url ?? '').length;
}

function trimSlash(value: string): string {
  return value.replace(/\/+$/, '');
}

function pathFromUrl(url: string | null | undefined): string {
  if (!url) {
    return '';
  }

  try {
    return trimSlash(new URL(url, 'https://example.com').pathname);
  } catch {
    return '';
  }
}
